/**
 * Barramento de eventos do nucleo.
 *
 * Os plugins nao se importam uns aos outros (regra 8): falam por eventos. Isso
 * so funciona se o barramento for a prova de bala — um ouvinte mal escrito no
 * plugin de financas nao pode impedir o plugin de saude de receber o seu evento,
 * nem quebrar o emissor. Por isso cada handler corre dentro do seu try/catch.
 *
 * O despacho e sincrono de proposito: a interface tem de reagir na mesma volta
 * do event loop em que o registro e escrito, senao pisca.
 */
import {
  CoreEvents,
  type EventBusLike,
  type Logger,
  type RecordChangedPayload,
  type SessionInfo,
} from '@flos/shared';

/** Eventos que o nucleo emite e que nao cabem no catalogo partilhado. */
export const CoreLocalEvents = {
  /** Uma definicao mudou. payload: SettingsChangedPayload */
  SETTINGS_CHANGED: 'core.settings.changed',
  /** A configuracao regional mudou. payload: LocaleConfig */
  LOCALE_CHANGED: 'core.locale.changed',
  /** Um plugin falhou o arranque e foi ignorado. payload: PluginFailurePayload */
  PLUGIN_FAILED: 'core.plugin.failed',
  /** Todos os plugins terminaram o registro. payload: PluginBootReport */
  PLUGINS_STARTED: 'core.plugins.started',
} as const;

export interface SessionChangedPayload {
  previous: SessionInfo | null;
  current: SessionInfo | null;
}

export interface PluginFailurePayload {
  pluginId: string;
  reason: string;
}

/**
 * Mapa evento -> payload. So os eventos com forma conhecida entram aqui; os
 * restantes ficam `unknown`, o que obriga quem ouve a validar antes de usar.
 */
export interface EventPayloads {
  [CoreEvents.RECORD_CHANGED]: RecordChangedPayload;
  [CoreEvents.SESSION_CHANGED]: SessionChangedPayload;
  [CoreEvents.CONNECTIVITY_CHANGED]: { online: boolean };
  [CoreLocalEvents.PLUGIN_FAILED]: PluginFailurePayload;
}

export type PayloadOf<E extends string> = E extends keyof EventPayloads
  ? EventPayloads[E]
  : unknown;

export type Unsubscribe = () => void;

type AnyHandler = (payload: never) => void;

export interface EventBusOptions {
  logger?: Logger;
  /** Limite de reentrancia. Um ciclo de eventos e sempre um erro de desenho. */
  maxDepth?: number;
}

export class EventBus implements EventBusLike {
  readonly #handlers = new Map<string, Set<AnyHandler>>();
  #logger: Logger | undefined;
  readonly #maxDepth: number;
  #depth = 0;

  constructor(options: EventBusOptions = {}) {
    this.#logger = options.logger;
    this.#maxDepth = options.maxDepth ?? 32;
  }

  /** O logger so existe depois do arranque; permitimos ligá-lo mais tarde. */
  setLogger(logger: Logger): void {
    this.#logger = logger;
  }

  on<E extends string>(event: E, handler: (payload: PayloadOf<E>) => void): Unsubscribe {
    let set = this.#handlers.get(event);
    if (!set) {
      set = new Set();
      this.#handlers.set(event, set);
    }
    const entry = handler as AnyHandler;
    set.add(entry);
    return () => {
      const current = this.#handlers.get(event);
      if (!current) return;
      current.delete(entry);
      if (current.size === 0) this.#handlers.delete(event);
    };
  }

  /** Ouve uma unica vez. Devolve o cancelamento, caso nunca chegue a disparar. */
  once<E extends string>(event: E, handler: (payload: PayloadOf<E>) => void): Unsubscribe {
    const off = this.on(event, (payload: PayloadOf<E>) => {
      off();
      handler(payload);
    });
    return off;
  }

  emit<E extends string>(event: E, payload?: PayloadOf<E>): void {
    const set = this.#handlers.get(event);
    if (!set || set.size === 0) return;

    if (this.#depth >= this.#maxDepth) {
      // Reentrancia descontrolada: normalmente A emite B que emite A. Cortamos
      // aqui em vez de estourar a pilha, e deixamos rasto para quem depurar.
      this.#logger?.error('Ciclo de eventos detetado; despacho interrompido', {
        event,
        depth: this.#depth,
      });
      return;
    }

    // Copia defensiva: um handler pode remover-se (ou remover outro) a meio.
    const snapshot = [...set];
    this.#depth++;
    try {
      for (const handler of snapshot) {
        try {
          (handler as (p: unknown) => void)(payload);
        } catch (error) {
          this.#reportHandlerError(event, error);
        }
      }
    } finally {
      this.#depth--;
    }
  }

  /**
   * Espera pelo proximo evento. Usado em testes e no arranque (por exemplo,
   * esperar por `core.app.ready` antes de mostrar a tela inicial).
   */
  waitFor<E extends string>(event: E, timeoutMs = 10_000): Promise<PayloadOf<E>> {
    return new Promise<PayloadOf<E>>((resolve, reject) => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      const off = this.on(event, (payload: PayloadOf<E>) => {
        if (timer !== undefined) clearTimeout(timer);
        off();
        resolve(payload);
      });
      if (timeoutMs > 0 && Number.isFinite(timeoutMs)) {
        timer = setTimeout(() => {
          off();
          reject(new Error(`O evento "${event}" nao chegou em ${timeoutMs} ms.`));
        }, timeoutMs);
        // Nao segurar o processo de testes so por causa de uma espera pendente.
        (timer as unknown as { unref?: () => void }).unref?.();
      }
    });
  }

  /** Quantos ouvintes tem um evento. Diagnostico e testes. */
  listenerCount(event: string): number {
    return this.#handlers.get(event)?.size ?? 0;
  }

  /** Liberta tudo. Chamado no encerramento do aplicativo. */
  clear(): void {
    this.#handlers.clear();
  }

  #reportHandlerError(event: string, error: unknown): void {
    const message = error instanceof Error ? error.message : String(error);
    this.#logger?.error('Ouvinte de evento falhou; os restantes continuaram', {
      event,
      erro: message,
    });
    if (!this.#logger) {
      // Nunca engolir um erro em silencio (regra de erros das normas).
      console.error(`[flos] ouvinte de "${event}" falhou:`, error);
    }
    // Reemitir o erro so quando o evento nao e ja o de erro, para nao recursar.
    if (event !== CoreEvents.ERROR) {
      this.emit(CoreEvents.ERROR, { origem: event, erro: message } as PayloadOf<
        typeof CoreEvents.ERROR
      >);
    }
  }
}
